import React, { useEffect, useState } from "react";

// 대시보드 "AI 안전 감지" 카드 프리뷰 (7일 추이형) — 최근 7일 감지 건수 추이 라인(실데이터).
//   [실시간 감시 · 감시 대수] + [7일 감지 추이 스파크라인(오늘 끝점 강조)] + [요일 축].
//   ※ 준수율 일별은 아직 미집계(기능 신규)라 '감지 건수' 추이로 표시. 데이터: /api/safety-report(7일 daily).

function pad2(n) {
  return String(n).padStart(2, "0");
}
function isoOf(dt) {
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}
const DOW = ["일", "월", "화", "수", "목", "금", "토"];

export default function AiSafetyPreview({ cameraCount = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const to = isoOf(now);
        const fromDt = new Date(now);
        fromDt.setDate(fromDt.getDate() - 6);
        const from = isoOf(fromDt);
        const res = await fetch(
          `/api/safety-report?from=${from}&to=${to}&site=all&limit=1`,
          { credentials: "include", cache: "no-store" }
        );
        const j = res.ok ? await res.json() : null;
        if (!cancelled) setData(j);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-gray-400">
        AI 감지 현황 불러오는 중…
      </div>
    );
  }

  const daily = (data?.daily || []).slice(-7);
  const counts = daily.map((d) => d.count || 0);
  const todayCount = counts.length ? counts[counts.length - 1] : 0;
  const maxC = Math.max(1, ...counts);
  const fleet = Number(cameraCount || 0);

  const W = 200;
  const H = 60;
  const pad = 8;
  const n = counts.length;
  const pts = counts.map((c, i) => {
    const x = n > 1 ? (i / (n - 1)) * (W - 2 * pad) + pad : W / 2;
    const y = H - pad - (c / maxC) * (H - 2 * pad);
    return [x, y];
  });
  const linePts = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const areaPts = pts.length
    ? `${pad},${H - pad} ${linePts} ${pts[pts.length - 1][0].toFixed(1)},${H - pad}`
    : "";
  const lastPt = pts.length ? pts[pts.length - 1] : null;
  const dows = daily.map((d) => {
    const [y, m, dd] = String(d.date).split("-").map(Number);
    return DOW[new Date(y, (m || 1) - 1, dd || 1).getDay()];
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* 상단: 실시간 감시 + 감시 대수 */}
      <div className="flex shrink-0 items-center justify-between">
        <div className="inline-flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-500" />
          </span>
          <span className="text-[11px] font-semibold text-cyan-600 dark:text-cyan-300">
            실시간 AI 감시 중
          </span>
        </div>
        {fleet > 0 && (
          <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold text-cyan-600 dark:bg-white/10 dark:text-cyan-300">
            {fleet}대
          </span>
        )}
      </div>

      <div className="mt-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
        7일 감지 추이 · <b className="text-cyan-600 dark:text-cyan-300">오늘 {todayCount}건</b>
      </div>

      {/* 스파크라인 (실데이터) */}
      <div className="min-h-0 flex-1">
        <svg viewBox="0 0 200 60" preserveAspectRatio="none" className="h-full w-full">
          {areaPts && <polygon points={areaPts} className="fill-cyan-500/10" />}
          <polyline
            points={linePts}
            fill="none"
            className="stroke-cyan-500 dark:stroke-cyan-400"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {lastPt && <circle cx={lastPt[0]} cy={lastPt[1]} r="3.5" className="fill-cyan-600 dark:fill-cyan-300" />}
        </svg>
      </div>

      {/* 요일 축 */}
      <div className="flex justify-between text-[9px] text-gray-400">
        {dows.map((w, i) => (
          <span key={i} className={i === dows.length - 1 ? "font-bold text-cyan-500 dark:text-cyan-300" : ""}>
            {i === dows.length - 1 ? "오늘" : w}
          </span>
        ))}
      </div>
    </div>
  );
}
